import { z } from 'zod';
import type { NeedKey } from './play';
import { KIN_KINDS } from './children';

// People (the Profile's People tab, and the "Who's here" list at a place): the players your character knows,
// how close you are, and the things you can do together. Closeness is one number per pair, grown by social
// actions and fading when you do nothing together; partner and family are agreed by both sides.

export const CLOSENESS_MAX = 100;
// closeness fades this much per day apart (worked out on read, like a skill's rust)
export const CLOSENESS_FADE_PER_DAY = 1;
export const closenessNow = (stored: number, lastAt: Date | string | null, now = new Date()) => {
  if (!lastAt) return stored;
  const days = Math.max(0, (now.getTime() - new Date(lastAt).getTime()) / 86_400_000);
  return Math.max(0, Math.round(stored - Math.floor(days) * CLOSENESS_FADE_PER_DAY));
};

// what closeness means, when nothing more has been agreed
export const TIES = {
  stranger: { label: 'Stranger', from: 0 },
  acquaintance: { label: 'Acquaintance', from: 1 },
  friend: { label: 'Friend', from: 20 },
  close_friend: { label: 'Close friend', from: 50 },
} as const;
export const AGREED_TIES = {
  partner: { label: 'Partner', icon: '💞', needs: 30 },
  family: { label: 'Family', icon: '🏡', needs: 20 },
} as const;
export type AgreedTie = keyof typeof AGREED_TIES;
export type Tie = keyof typeof TIES | AgreedTie;
export const tieFor = (closeness: number, agreed: AgreedTie | null): Tie => {
  if (agreed) return agreed;
  if (closeness >= TIES.close_friend.from) return 'close_friend';
  if (closeness >= TIES.friend.from) return 'friend';
  if (closeness >= TIES.acquaintance.from) return 'acquaintance';
  return 'stranger';
};
export const TIE_LABELS: Record<Tie, string> = {
  stranger: 'Stranger',
  acquaintance: 'Acquaintance',
  friend: 'Friend',
  close_friend: 'Close friend',
  partner: 'Partner',
  family: 'Family',
};

// the social actions (Lagos Life's say hello, gist, crack a joke, throw shade): both must be in the same place
// and the other person must have been seen in the last few minutes. Each changes closeness and both people's
// needs; shade is the one that costs closeness.
type Effects = Partial<Record<NeedKey, number>>;
export const SOCIAL_ACTIONS = {
  say_hello: { label: 'Say hello', icon: '👋', closeness: 3, me: { social: 4 }, them: { social: 4 }, seconds: 5 },
  gist: {
    label: 'Gist',
    icon: '🗣️',
    closeness: 6,
    me: { social: 8, fun: 4 },
    them: { social: 8, fun: 4 },
    seconds: 20,
  },
  crack_joke: {
    label: 'Crack a joke',
    icon: '😂',
    closeness: 5,
    me: { fun: 8, social: 4 },
    them: { fun: 10 },
    seconds: 10,
  },
  throw_shade: { label: 'Throw shade', icon: '😏', closeness: -8, me: { fun: 6 }, them: { fun: -6 }, seconds: 10 },
} as const satisfies Record<
  string,
  { label: string; icon: string; closeness: number; me: Effects; them: Effects; seconds: number }
>;
export type SocialActionKey = keyof typeof SOCIAL_ACTIONS;
export const SOCIAL_ACTION_KEYS = Object.keys(SOCIAL_ACTIONS) as SocialActionKey[];
// the same thing to the same person: once every 10 minutes; and no more than this many social actions an hour
export const SOCIAL_COOLDOWN_SECONDS = 600;
export const SOCIAL_PER_HOUR = 40;
// "here" means seen in the last 5 minutes at the same place
export const HERE_WINDOW_SECONDS = 300;
// crack a joke trains comedy
export const SOCIAL_SKILL_XP: Partial<Record<SocialActionKey, { skill: string; xp: number }>> = {
  crack_joke: { skill: 'comedy', xp: 5 },
};

// ---- the API

// a person as another player sees them (never their email or anything from the account)
export const PersonView = z.object({
  lifeId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
  // the look, for a small picture of them
  look: z.object({
    skinTone: z.number().int(),
    outfit: z.string(),
    hair: z.string(),
    hairColour: z.string(),
    // the body, so they can be drawn in 3D in a venue (older replies may not have them)
    frame: z.string().optional(),
    build: z.string().optional(),
  }),
  closeness: z.number().int(),
  tie: z.enum(['stranger', 'acquaintance', 'friend', 'close_friend', 'partner', 'family']),
  // family: what they are to you (sibling, cousin…; null for family agreed before kinds existed)
  kin: z.enum(KIN_KINDS).nullable(),
  // a partner or family request waiting: who asked, and for family what they would be to you
  pending: z
    .object({ tie: z.enum(['partner', 'family']), askedByMe: z.boolean(), kin: z.enum(KIN_KINDS).nullable() })
    .nullable(),
  lastAt: z.string().nullable(),
  here: z.boolean(),
  blocked: z.boolean(),
});
export type PersonView = z.infer<typeof PersonView>;
export const PeopleReply = z.object({
  // everyone you know, closest first
  people: z.array(PersonView),
  // players at your place right now (known or not)
  here: z.array(PersonView),
  placeId: z.string(),
});
export type PeopleReply = z.infer<typeof PeopleReply>;
// who is at a place right now: how many players, and the first few of them (most recently seen first)
export const PLACE_HERE_SHOWN = 6;
export const PlaceHereReply = z.object({
  placeId: z.string(),
  count: z.number().int().min(0),
  people: z.array(PersonView).max(PLACE_HERE_SHOWN),
});
export type PlaceHereReply = z.infer<typeof PlaceHereReply>;

export const PersonAction = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('social'),
      kind: z.enum(SOCIAL_ACTION_KEYS as [SocialActionKey, ...SocialActionKey[]]),
    })
    .strict(),
  // asking to be family says how: `kin` is what they would be to you (they agree, or not)
  z
    .object({ action: z.literal('ask'), tie: z.enum(['partner', 'family']), kin: z.enum(KIN_KINDS).optional() })
    .strict(),
  z.object({ action: z.literal('accept') }).strict(),
  z.object({ action: z.literal('decline') }).strict(),
  // end a partnership or leave a family: back to what closeness says
  z.object({ action: z.literal('end_tie') }).strict(),
  z.object({ action: z.literal('block') }).strict(),
  z.object({ action: z.literal('unblock') }).strict(),
]);
export type PersonAction = z.infer<typeof PersonAction>;
export const PersonActionReply = z.object({ person: PersonView, message: z.string() });
export type PersonActionReply = z.infer<typeof PersonActionReply>;
